import * as React from 'react';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useRequest } from 'redux-query-react';
import { useProductData } from '../hooks/useProductData';
import { useCartOperations } from '../hooks/useCartOperations';
import { useProductConfiguration } from '../hooks/useProductConfiguration';
import useAuth from '../hooks/useAuth';
import getBuyBackProductsRequest from '../components/BuyBack/requests/getBuyBackProductsRequest';
import { IDetailViewConfig, IProductConfig, IConfigPrice } from '../types/product/detail.types';
import { ProductSelectedInfo } from '../types/product.types';
import { IBuyBackProduct } from 'components/BuyBack/interfaces/buyBackInterfaces';
import { IEstoSlice3Config } from 'components/Catalog/Product/DetailComponent/EstoSlice3/EstoSlice3Option';
import { ISliceConfiguration } from 'components/Catalog/Product/DetailComponent/Slice3/Slice3ConfigReducer';
import { ISwatchConfig } from 'components/Catalog/Product/Configurator';
import { IImage } from 'components/Catalog/Interfaces/IImage';
import { CartType } from 'components/Cart/Enum/CartType';
import { SESSION_STORAGE_KEYS } from '../constants/product.constants';
import { formatPrice } from '../utils/price.utils';
import { trackProductView, trackProductConfigurationChange, prepareProductTrackingData } from '../utils/tracking.utils';
import { IInbankRentalData } from 'components/Catalog/Product/CartWrapper';

interface RootState {
    entities?: {
        estoSlice3Request?: IEstoSlice3Config;
        buyBackProducts?: {
            devices?: IBuyBackProduct[];
        };
    };
}

interface ProductContextType {
    // Product Data
    productConfig: IProductConfig;
    selectedProductInformation: ProductSelectedInfo;
    setSelectedProductInformation: (info: ProductSelectedInfo) => void;

    // Pricing
    price: IConfigPrice;
    oldPrice?: string;

    // Gallery
    images: IImage[] | undefined;
    initialGalleryHtml?: string;

    // Messages
    error?: string;
    setError: (error?: string) => void;
    success?: string;
    setSuccess: (success?: string) => void;

    // Configuration
    isConfigured: boolean;

    // Cart Operations
    cartFlowType: string;
    setCartFlowType: (type: string) => void;
    addToCart: (cartFlowTypePassed?: string) => Promise<void>;
    addToCartInbankRental: () => Promise<void>;
    handleBuyBackProducts: () => void;
    addBuyBackProductsReq: (devices: IBuyBackProduct[]) => Promise<{ status: number; body: unknown }>;

    // Buy Back
    buyBackProducts: IBuyBackProduct[];
    setBuyBackProducts: (products: IBuyBackProduct[]) => void;
    skipBuyBack: boolean;
    setSkipBuyBack: (skip: boolean) => void;
    addBuyBackProducts: boolean;
    setAddBuyBackProducts: (add: boolean) => void;
    savedBuyBackProducts?: IBuyBackProduct[];

    // States
    isLoadingAvailability: boolean;
    setIsLoadingAvailability: (loading: boolean) => void;

    // Redux state
    estoSlice3Config?: IEstoSlice3Config;
    slice3Config?: ISliceConfiguration;
    setSlice3Config: (config?: ISliceConfiguration) => void;

    // Config sections
    availabilityConfig?: IDetailViewConfig['availabilityConfig'];
    inbankRentalConfig?: IDetailViewConfig['inbankRentalConfig'];
    inbankRentalEnabled: boolean;
    slice3MethodEnabled: boolean;
    labels: IDetailViewConfig['labels'];
    smartdealB2BConfig: IDetailViewConfig['smartdealB2BConfig'];
    swatchConfig: IDetailViewConfig['swatchConfig'];
    financialConfig: IDetailViewConfig['financialConfig'];
    buyBackConfig?: IDetailViewConfig['buyBackConfig'];

    // Purchase validation
    disablePurchase: () => boolean;

    // Inbank rental dynamic data
    inbankRentalData?: IInbankRentalData;
    setInbankRentalData: (data?: IInbankRentalData) => void;
}

const ProductContext = createContext<ProductContextType | null>(null);

interface ProductProviderProps {
    children: ReactNode;
    config: IDetailViewConfig;
    dom: string;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children, config, dom }) => {
    const { customer, isCustomerRequestFinished } = useAuth();
    const { estoSlice3Config, savedBuyBackProducts } = useSelector((state: RootState) => ({
        estoSlice3Config: state.entities?.estoSlice3Request,
        savedBuyBackProducts: state.entities?.buyBackProducts?.devices,
    }));

    // Local state
    const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);
    const [buyBackProducts, setBuyBackProducts] = useState<IBuyBackProduct[]>([]);
    const [skipBuyBack, setSkipBuyBack] = useState(false);
    const [addBuyBackProducts, setAddBuyBackProducts] = useState(false);
    const [slice3Config, setSlice3Config] = useState<ISliceConfiguration | undefined>();
    const [inbankRentalData, setInbankRentalData] = useState<IInbankRentalData | undefined>();

    // Extract config sections
    const {
        productConfig,
        availabilityConfig,
        inbankRentalConfig,
        inbankRentalEnabled,
        slice3MethodEnabled,
        labels,
        smartdealB2BConfig,
        swatchConfig,
        financialConfig,
        buyBackConfig,
    } = config;

    // Parse DOM for gallery
    const parser = new DOMParser();
    const doc = parser.parseFromString(dom, 'text/html');
    const initialGalleryDom = doc.querySelector('[data-component="gallery"]')?.innerHTML;

    // Format old price
    const priceOld = productConfig.priceOld
        ? formatPrice(parseFloat(productConfig.priceOld), productConfig.priceFormat)
        : undefined;

    // Get cart flow type
    const getFlowType = (): string => {
        if (config.inbankRentalConfig?.monthlyFee) {
            return window.sessionStorage.getItem(SESSION_STORAGE_KEYS.CART_FLOW) ?? CartType.REGULAR_CART;
        }
        return CartType.REGULAR_CART;
    };

    const [cartFlowType, setCartFlowType] = useState(getFlowType);

    // Use existing hooks
    const {
        selectedProductInformation,
        setSelectedProductInformation,
        price,
        oldPrice,
        images,
        initialGalleryHtml,
        error,
        setError,
        success,
        setSuccess,
    } = useProductData(productConfig.price, priceOld, initialGalleryDom, productConfig.priceFormat);

    const { addToCart, addToCartInbankRental, handleBuyBackProducts, addBuyBackProductsReq } = useCartOperations({
        productConfig,
        selectedProductInformation,
        customer,
        buyBackProducts,
        cartFlowType,
        inbankRentalMetaResponse: inbankRentalData?.metaResponse,
        onSuccess: setSuccess,
        onError: setError,
    });

    const { isConfigured } = useProductConfiguration(config.swatchConfig, setSelectedProductInformation);

    // Request buy back products
    useRequest(getBuyBackProductsRequest());

    // Purchase validation logic
    const disablePurchase = (): boolean => {
        if (isLoadingAvailability) {
            return true;
        }

        if (!isConfigured) {
            return true;
        }

        if (skipBuyBack) {
            return false;
        }

        if (!buyBackConfig) {
            return false;
        }

        if (!addBuyBackProducts) {
            return true;
        }

        if (addBuyBackProducts && !buyBackProducts.length) {
            return true;
        }

        return false;
    };

    // Effects
    useEffect(() => {
        if (!savedBuyBackProducts) {
            return;
        }
        setBuyBackProducts(savedBuyBackProducts);
        setAddBuyBackProducts(savedBuyBackProducts.length > 0);
    }, [savedBuyBackProducts]);

    useEffect(() => {
        if (isCustomerRequestFinished) {
            const trackingData = prepareProductTrackingData(productConfig, productConfig.priceOld);
            trackProductView(trackingData, customer?.id?.toString());
        }
    }, [isCustomerRequestFinished, productConfig, customer]);

    useEffect(() => {
        if (selectedProductInformation) {
            trackProductConfigurationChange({
                productConfig,
                selectedProductInformation,
                swatchConfig: config?.swatchConfig,
            });
        }
    }, [selectedProductInformation, productConfig, config.swatchConfig]);

    const contextValue: ProductContextType = {
        // Product Data
        productConfig,
        selectedProductInformation,
        setSelectedProductInformation,

        // Pricing
        price,
        oldPrice,

        // Gallery
        images,
        initialGalleryHtml,

        // Messages
        error,
        setError,
        success,
        setSuccess,

        // Configuration
        isConfigured,

        // Cart Operations
        cartFlowType,
        setCartFlowType,
        addToCart,
        addToCartInbankRental,
        handleBuyBackProducts,
        addBuyBackProductsReq,

        // Buy Back
        buyBackProducts,
        setBuyBackProducts,
        skipBuyBack,
        setSkipBuyBack,
        addBuyBackProducts,
        setAddBuyBackProducts,
        savedBuyBackProducts,

        // States
        isLoadingAvailability,
        setIsLoadingAvailability,

        // Redux state
        estoSlice3Config,
        slice3Config,
        setSlice3Config,

        // Config sections
        availabilityConfig,
        inbankRentalConfig,
        inbankRentalEnabled,
        slice3MethodEnabled,
        labels,
        smartdealB2BConfig,
        swatchConfig: swatchConfig || ({} as ISwatchConfig),
        financialConfig,
        buyBackConfig,

        // Purchase validation
        disablePurchase,

        // Inbank rental dynamic data
        inbankRentalData,
        setInbankRentalData,
    };

    return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>;
};

export const useProductContext = (): ProductContextType => {
    const context = useContext(ProductContext);
    if (!context) {
        throw new Error('useProductContext must be used within a ProductProvider');
    }
    return context;
};
